import React from 'react';
import HeaderIconTooltip from '../componentsV2/HeaderIconTooltip/HeaderIconTooltip';
import Button from '../componentsV2/Button/Button';
import { isEmail } from './isEmail';

/**
 *function to create an object of errors
 *for empty input fields
 * @param {object} formData
 * @param {object} errorMessage
 * @param {function} setErrorMessage
 * @returns {object} with errors
 */

export const handleErrorMessages = (formData) => {
  const errors = {};
  if (formData.name_first === '') {
    errors.name_first = 'Please enter the user’s first name';
  }
  if (formData.name_last === '') {
    errors.name_last = 'Please enter the user’s last name';
  }
  if (formData.email === '') {
    errors.email = 'Please enter the user’s email';
  }
  if (formData.email !== '' && !isEmail(formData.email)) {
    errors.email = 'Please enter a valid email address';
  }
  if (formData.role === '') {
    errors.role = 'Choose a role for this user';
  }
  return errors;
};

/**
 * the function to create a toast message
 * that lists all the fields that have been updated for user
 * @param {object} userData
 * @param {object} formData
 * @returns {string} with toast message
 */

export const handleToastMessages = (userData, newUserData, userId, organizationId, verificationEmailSentMessage = null, pendingEmailForVerification = null) => {
  // Existing user was just added to org
  if (newUserData?.organizations?.length > 1 && userId === undefined) {
    return 'User already exists and has been added to the organization.';
  }
  // New created user
  if (userId === undefined) {
    return 'An email has been sent to the new user for account confirmation. The link will expire in 24 hours.';
  }

  const changedData = [];
  const newEmail = newUserData.unconfirmed_email?.toLowerCase() || newUserData.email?.toLowerCase();

  const emailChanged = (newEmail !== userData.email?.toLowerCase() && newEmail !== userData.unconfirmed_email?.toLowerCase()) || verificationEmailSentMessage;

  if (userData.name_first?.toLowerCase() !== newUserData.name_first?.toLowerCase()) {
    changedData.push('first name');
  }
  if (userData.name_last?.toLowerCase() !== newUserData.name_last?.toLowerCase()) {
    changedData.push('last name');
  }
  const getRoleFromOrgs = (user, orgId) => user.user_organizations?.find(uo => uo.organization_id === orgId)?.role?.toLowerCase() || '';

  const oldRole = getRoleFromOrgs(userData, organizationId);
  const newRole = getRoleFromOrgs(newUserData, organizationId);

  if (oldRole !== newRole) {
    changedData.push('role');
  }
  let message = '';

  if (changedData.length > 0) {
    // Function to format the changed fields correctly
    const formatChangedData = (changedText) => {
      if (changedText.length === 1) {
        return changedText[0];
      }
      if (changedText.length === 2) {
        return changedText.join(' and ');
      }
      return `${changedText.slice(0, -1).join(', ')}, and ${changedText.slice(-1)}`;
    };

    const changedDataString = formatChangedData(changedData);
    const verb = changedData.length > 1 ? 'have' : 'has'; // Correct grammar

    const capitalize = (str) => str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();

    message = `${capitalize(newUserData.name_first)} ${capitalize(newUserData.name_last)}'s ${changedDataString} ${verb} been updated.`;
  }

  if (emailChanged) {
    const emailMessage = ` A verification link has been sent to ${pendingEmailForVerification !== newEmail ? pendingEmailForVerification : newEmail}.`;

    // If other fields were changed, append email message
    message = message ? `${message} ${emailMessage}` : emailMessage;
  }

  return message;
};

/**
 * the columns for the users table
 */

export const getColumns = (organizationId, onOpenAccountDetailsModal) => [
  {
    title: 'First Name', dataIndex: 'name_first', isSortable: false,
  },
  {
    title: 'Last Name', dataIndex: 'name_last', isSortable: false,
  },
  {
    title: 'Email', dataIndex: 'email', isSortable: false,
  },
  {
    title: 'Role',
    dataIndex: 'user_organizations',
    isSortable: false,
    render: (userOrganizations) => {
      const role = userOrganizations?.find(
        (uo) => uo.organization_id === organizationId,
      )?.role;
      return role ? role.charAt(0).toUpperCase() + role.slice(1) : '—';
    },
    tooltip: <HeaderIconTooltip
      heading=""
      divTextStart="Find the full list of role access"
      divTextEnd="permissions in the"
      linkUrl="https://docs.google.com/document/d/1M7dB9txljYhpIDLA7gDzMoGdD2qVJ7XZcnvUSu7GwUY/edit?tab=t.0#heading=h.5xyfon1j6ts8"
      linkText="Vamos Guide"
      iconName="icon_circle"
      border=""
      margins=""
      paddingBottom=""
      tooltipId="role-tooltip"
    />,
  },
  {
    title: 'Date Registered',
    dataIndex: 'confirmed_at',
    isSortable: false,
    render: (date, user) => (
      <div className="flex items-center gap-1">
        {date !== null
          ? (
            <div
              role="button"
              tabIndex="0"
              onClick={(e) => {
                e.stopPropagation();
                onOpenAccountDetailsModal(user);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.stopPropagation();
                  onOpenAccountDetailsModal(user);
                }
              }}
            >
              <span>{date}</span>
            </div>
          )
          : (
            <Button
              label="Resend..."
              onClick={(e) => {
                e.stopPropagation();
                onOpenAccountDetailsModal(user);
              }}
              color="secondary"
              size="sm"
            />
          )}
      </div>
    ),
    tooltip: <HeaderIconTooltip
      heading=""
      divTextStart="Click a date to see when the "
      divTextEnd={(
        <>
          <div>user completed registration. </div>
          <div>Click “Resend” to send them a </div>
          <div>new invitation email.</div>
        </>
      )}
      iconName="icon_circle"
      border=""
      margins=""
      paddingBottom=""
      tooltipId="date-tooltip"
    />,
  },
];
